import React, { useState } from 'react';
import { PhoneIcon, MailIcon, MapPinIcon, UserIcon, SendIcon, CheckCircleIcon, FileTextIcon, LoaderIcon, AlertCircleIcon } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../context/LanguageContext';
import { ScrollFadeIn } from './ScrollFadeIn';
const EMAILJS_SERVICE_ID = 'service_w18a8tv';
const EMAILJS_TEMPLATE_ID = 'template_b7pnymb';
const EMAILJS_PUBLIC_KEY = '-wcHDgLwzD7V-m3Ay';
export function Contact() {
  const {
    t
  } = useLanguage();
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const date = new Date().toLocaleString();
      const fullMessage = `
Date: ${date}

Company: ${formData.company || 'Not provided'}
Phone: ${formData.phone || 'Not provided'}
Email: ${formData.email}

Message:
${formData.message}
      `;
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        name: formData.name,
        message: fullMessage
      }, EMAILJS_PUBLIC_KEY);
      setStatus('success');
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatus('error');
    }
  };
  return <section id="contact" className="w-full bg-navy py-20 sm:py-28" data-id="element-72">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-73">
        <ScrollFadeIn data-id="element-74">
          <div className="mb-4" data-id="element-75">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-electric" data-id="element-76">
              {t('contact.label')}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white mb-3" data-id="element-77">
            {t('contact.heading')}
          </h2>
          <p className="text-slate-400 max-w-2xl mb-12" data-id="element-78">
            {t('contact.subtitle')}
          </p>
        </ScrollFadeIn>

        <div className="grid lg:grid-cols-2 gap-12" data-id="element-79">
          <ScrollFadeIn delay={100} data-id="element-80">
            {status === 'success' ? <div className="flex flex-col items-center justify-center h-full p-8 bg-graphite border border-slate-700/50 rounded text-center" data-id="element-81">
                <CheckCircleIcon className="w-12 h-12 text-electric mb-4" data-id="element-82" />
                <p className="text-lg font-semibold text-white mb-2" data-id="element-83">
                  {t('contact.form.success')}
                </p>
                <button onClick={() => setStatus('idle')} className="mt-4 text-sm text-electric hover:text-sky-400 transition-colors" data-id="element-84">
                  {t('contact.form.sendAnother')}
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-85">
                {status === 'error' && <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded" data-id="element-86">
                    <AlertCircleIcon className="w-5 h-5 text-red-400 shrink-0" data-id="element-87" />
                    <p className="text-sm text-red-300" data-id="element-88">
                      {t('contact.form.error')}
                    </p>
                  </div>}

                <div className="grid sm:grid-cols-2 gap-4" data-id="element-89">
                  <input name="name" type="text" required placeholder={t('contact.form.name')} value={formData.name} onChange={handleChange} disabled={status === 'sending'} className="w-full px-4 py-3 bg-graphite border border-slate-700/50 rounded text-sm text-white focus:outline-none focus:border-electric/50 transition-colors" data-id="element-90" />

                  <input name="company" type="text" placeholder={t('contact.form.company')} value={formData.company} onChange={handleChange} disabled={status === 'sending'} className="w-full px-4 py-3 bg-graphite border border-slate-700/50 rounded text-sm text-white focus:outline-none focus:border-electric/50 transition-colors" data-id="element-91" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4" data-id="element-92">
                  <input name="email" type="email" required placeholder={t('contact.form.email')} value={formData.email} onChange={handleChange} disabled={status === 'sending'} className="w-full px-4 py-3 bg-graphite border border-slate-700/50 rounded text-sm text-white focus:outline-none focus:border-electric/50 transition-colors" data-id="element-93" />

                  <input name="phone" type="tel" placeholder={t('contact.form.phone')} value={formData.phone} onChange={handleChange} disabled={status === 'sending'} className="w-full px-4 py-3 bg-graphite border border-slate-700/50 rounded text-sm text-white focus:outline-none focus:border-electric/50 transition-colors" data-id="element-94" />
                </div>

                <textarea name="message" rows={5} required placeholder={t('contact.form.message')} value={formData.message} onChange={handleChange} disabled={status === 'sending'} className="w-full px-4 py-3 bg-graphite border border-slate-700/50 rounded text-sm text-white resize-none focus:outline-none focus:border-electric/50 transition-colors" data-id="element-95" />

                <button type="submit" disabled={status === 'sending'} className="inline-flex items-center gap-2 px-8 py-3.5 bg-electric text-white font-semibold text-sm uppercase tracking-wider rounded hover:bg-sky-600 transition-colors duration-200 disabled:opacity-60" data-id="element-96">
                  {status === 'sending' ? <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-97" /> : <SendIcon className="w-4 h-4" data-id="element-98" />}
                  {status === 'sending' ? t('contact.form.sending') : t('contact.form.submit')}
                </button>
              </form>}
          </ScrollFadeIn>

          <ScrollFadeIn delay={200} data-id="element-99">
            <div className="space-y-6" data-id="element-100">
              <div className="flex items-start gap-4" data-id="element-101">
                <UserIcon className="w-4 h-4 text-electric" data-id="element-102" />
                <p className="text-sm text-white" data-id="element-103">
                  Power Logic Automation LLP
                </p>
              </div>

              <div className="flex items-start gap-4" data-id="element-104">
                <PhoneIcon className="w-4 h-4 text-electric" data-id="element-105" />
                <p className="text-sm text-white" data-id="element-106">
                  {t('contact.info.phone')}
                </p>
              </div>

              <div className="flex items-start gap-4" data-id="element-107">
                <MailIcon className="w-4 h-4 text-electric" data-id="element-108" />
                <p className="text-sm text-white" data-id="element-109">
                  {t('contact.info.email')}
                </p>
              </div>

              <div className="flex items-start gap-4" data-id="element-110">
                <MapPinIcon className="w-4 h-4 text-electric" data-id="element-111" />
                <p className="text-sm text-slate-300" data-id="element-112">
                  {t('contact.info.location')}
                </p>
              </div>

              <div className="flex items-start gap-4" data-id="element-113">
                <FileTextIcon className="w-4 h-4 text-electric" data-id="element-114" />
                <p className="text-sm text-slate-400" data-id="element-115">
                  {t('contact.info.bin')}
                </p>
              </div>
            </div>
          </ScrollFadeIn>
        </div>
      </div>
    </section>;
}